import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonCol,
  IonGrid,
  IonInput,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBlurNotifierDirective } from '@okcargo-apps/layout-ionic';
import { getEmptyFreightDetailForShipper, IFreightDetailedForShipper } from '@okcargo-apps/shared-domain';
import { ICostCenter } from '@okcargo-apps/shared-domain-ionic';
import { Subscription } from 'rxjs';
import { FreightShipperService } from '../freight-shipper.service';

@Component({
  selector: 'lib-freight-shipper-edit-general-form',
  templateUrl: './freight-shipper-edit-general-form.component.html',
  styleUrls: ['./freight-shipper-edit-general-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonList,
    CommonModule,
    TranslateModule,
    IonTextarea,
    IonToggle,
    IonInput,
    IonCol,
    IonRow,
    IonGrid,
    IonSelect,
    IonSelectOption,
    ReactiveFormsModule,
    FormBlurNotifierDirective,
  ],
})
export class FreightShipperEditGeneralFormComponent {
  public readonly freight = input<IFreightDetailedForShipper>(getEmptyFreightDetailForShipper());
  public readonly dataModified = output<IFreightDetailedForShipper>();

  @ViewChild('blurNotifier') blurNotifier!: FormBlurNotifierDirective;

  private readonly fb = inject(FormBuilder);
  private readonly freightShipService = inject(FreightShipperService);
  private readonly translate = inject(TranslateService);

  private readonly subscriptions = new Subscription();

  private readonly _freight = signal<IFreightDetailedForShipper>(getEmptyFreightDetailForShipper());

  public readonly costCenters = this.freightShipService.costCenters;

  public generalDataForm: FormGroup;

  public showTemperatureFields = false;

  constructor() {
    this.generalDataForm = this.createForm();

    this.generalDataForm
      .get('transportType')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((types: string[] | null) => {
        this.updateShowTemperatureFields(types || []);
      });

    effect(() => {
      const currentFreight = this.freight();
      const centers = this.costCenters();

      untracked(() => {
        this.populateForm(currentFreight, centers);
      });
    });
  }

  public validateForm(): boolean {
    if (this.generalDataForm.valid) {
      return true;
    }
    this.generalDataForm.markAllAsTouched();
    Object.values(this.generalDataForm.controls).forEach((control) => {
      control.markAsDirty();
    });
    return false;
  }

  public emitModifiedData(): void {
    const formValues = this.generalDataForm.value;

    const currentFreight = this._freight();

    const updatedFreight: IFreightDetailedForShipper = {
      ...currentFreight,
      reference: formValues.shipperFreightId,
      vehicleTypes: formValues.transportType || [],
      loadingTypes: formValues.loadingType || [],
      deliveryNoteInPaperRequired: formValues.deliveryNoteInPaper || false,
      waste: formValues.field_waste || false,
      instructions: formValues.instructions || '',
      // TODO implement this ??? review
      shipperSiteId: formValues.shipperCostCenter,
    };

    // TODO implement this
    // if (
    //   this.showTemperatureFields &&
    //   formValues.temperatureMin !== undefined &&
    //   formValues.temperatureMax !== undefined
    // ) {
    //   updatedFreight.temperatureMin = formValues.temperatureMin;
    //   updatedFreight.temperatureMax = formValues.temperatureMax;
    // }

    this.dataModified.emit(updatedFreight);
  }

  public resetForm(): void {
    this.generalDataForm.reset({
      deliveryNoteInPaper: false,
      field_waste: false,
    });

    this.updateShowTemperatureFields([]);

    // Leemos el valor síncrono del signal sin suscribirnos
    const centers = this.costCenters();
    if (centers && centers.length > 0) {
      this.generalDataForm.patchValue({
        shipperCostCenter: centers[0].id,
      });
    }
  }

  private createForm(): FormGroup {
    return (this.generalDataForm = this.fb.group({
      shipperCostCenter: [''],
      shipperFreightId: ['', Validators.required],
      transportType: ['', Validators.required],
      loadingType: ['', Validators.required],
      deliveryNoteInPaper: [false],
      field_waste: [false],
      instructions: [''],
    }));
  }

  private updateShowTemperatureFields(transportTypes: string[]): void {
    const isOnlyRefrigerated = transportTypes && transportTypes.length === 1 && transportTypes[0] === 'REFRIGERATED';

    this.showTemperatureFields = isOnlyRefrigerated;

    if (isOnlyRefrigerated) {
      this.generalDataForm.addControl('temperatureMin', new FormControl(null, Validators.required));
      this.generalDataForm.addControl('temperatureMax', new FormControl(null, Validators.required));
    } else {
      this.generalDataForm.removeControl('temperatureMin');
      this.generalDataForm.removeControl('temperatureMax');
    }
  }

  private populateForm(freight: IFreightDetailedForShipper, centers: ICostCenter[]): void {
    if (!freight || Object.keys(freight).length === 0 || !freight.reference) {
      this.generalDataForm.reset();
      if (centers && centers.length > 0) {
        this.generalDataForm.patchValue({
          shipperCostCenter: centers[0].id,
        });
      }
      return;
    }

    this.updateShowTemperatureFields(freight.vehicleTypes || []);

    const formData: any = {
      shipperFreightId: freight.reference || '',
      transportType: freight.vehicleTypes || [],
      loadingType: freight.loadingTypes || [],
      deliveryNoteInPaper: freight.deliveryNoteInPaperRequired || false,
      field_waste: freight.waste || false,
      instructions: freight.instructions || '',
      shipperCostCenter: freight.shipperSiteId || (centers.length > 0 ? centers[0].id : ''),

      // TODO review this, implement?
      // if (this.generalDataForm.contains('temperatureMin') && this.generalDataForm.contains('temperatureMax')) {
      //   formData.temperatureMax = freight.temperatureMax || null;
      //   formData.temperatureMin = freight.temperatureMin || null;
      // }
    };

    this.generalDataForm.patchValue(formData);
  }
}
